import {CommercialDimensions} from '@domain/commercial-dimensions';
import {BmpDrawing} from '@domain/production-schedule/bmp-drawing';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {EpDrawing} from '@domain/production-schedule/ep-drawing';
import {EpProductionScheduleItemInPathGroup} from '@domain/production-schedule/ep-production-schedule-item-in-path-group';
import {FinishingForProductionSchedule} from '@domain/production-schedule/finishing-for-production-schedule';
import {JuteProductionScheduleItemInPathGroup} from '@domain/production-schedule/jute-production-schedule-item-in-path-group';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {ManualNonProductionItemDrawing} from '@domain/production-schedule/manual-non-production-item-drawing';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {TechnicalDimensions} from '@domain/technical-dimensions';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {ListDrawingJute} from '@presentation/components/drawing-list/list-drawing-jute';
import {ListDrawingOrderLine} from '@presentation/components/drawing-list/list-drawing-order-line';
import {AssertionUtils, convertDentsToMillimeters, convertHeightInMeterToPicks} from '@vdw/angular-component-library';
import {calculateDrawingHeightInPicks} from './calculate-drawing-height-in-picks';
import {calculateDrawingWidthInDents} from './calculate-drawing-width-in-dents';

export function createProductionScheduleItemInPathGroupFromDrawing(
  finishingForProductionSchedule: FinishingForProductionSchedule,
  listDrawing: ListDrawing,
  machineQuality: MachineQualityForProductionSchedule
): ProductionScheduleItemInPathGroup {
  let productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup;

  const defaultQuantity = listDrawing.amount;
  const drawing = listDrawing.drawing;
  const orderLineId = listDrawing instanceof ListDrawingOrderLine ? listDrawing.orderLine.id : null;

  let technicalDimensions: TechnicalDimensions = {
    heightInPicks: calculateDrawingHeightInPicks(drawing, machineQuality),
    widthInDents: calculateDrawingWidthInDents(drawing, machineQuality)
  };
  let commercialDimensions: CommercialDimensions = null;

  if (listDrawing instanceof ListDrawingOrderLine && listDrawing?.orderLine?.article?.design?.repeated && !AssertionUtils.isNullOrUndefined(listDrawing?.rollLengthInMeter)) {
    let heightInPicks = convertHeightInMeterToPicks(listDrawing?.rollLengthInMeter, machineQuality?.pickDensity * 1000, machineQuality?.weaveStructure);

    while (heightInPicks % machineQuality?.weaveStructure?.sendRepeat !== 0) {
      heightInPicks++;
    }

    technicalDimensions = {
      heightInPicks: heightInPicks,
      widthInDents: listDrawing?.orderLine?.article?.technicalWidthInDents
    };

    commercialDimensions = {
      heightInMM: listDrawing?.rollLengthInMeter * 1000,
      widthInMM: listDrawing.orderLine?.article?.commercialWidthInMM
    };
  } else if (listDrawing instanceof ListDrawingOrderLine) {
    technicalDimensions = {
      heightInPicks: listDrawing.orderLine.article.technicalHeightInPicks,
      widthInDents: listDrawing.orderLine.article.technicalWidthInDents
    };

    commercialDimensions = {
      heightInMM: listDrawing.orderLine.article.commercialHeightInMM,
      widthInMM: listDrawing.orderLine.article.commercialWidthInMM
    };
  } else if (listDrawing instanceof ListDrawingJute) {
    commercialDimensions = {
      heightInMM: machineQuality.pickDensity > 0.0 ? technicalDimensions.heightInPicks / machineQuality.pickDensity : 0,
      widthInMM: convertDentsToMillimeters({
        valueInDents: technicalDimensions.widthInDents,
        factorForConversionFromMillimetersToDents: machineQuality.reedDensity
      })
    };
  } else if (listDrawing instanceof ListDrawingConfiguration && listDrawing?.drawing?.repeated && !AssertionUtils.isNullOrUndefined(listDrawing?.rollLengthInMeter)) {
    let heightInPicks = convertHeightInMeterToPicks(listDrawing.rollLengthInMeter, machineQuality.pickDensity * 1000, machineQuality.weaveStructure);

    while (heightInPicks % machineQuality?.weaveStructure?.sendRepeat !== 0) {
      heightInPicks++;
    }

    commercialDimensions = {
      heightInMM: listDrawing.rollLengthInMeter * 1000,
      widthInMM: listDrawing?.commercialDimensionsInMM?.widthInMM
    };
    technicalDimensions.heightInPicks = heightInPicks;
  } else if (listDrawing instanceof ListDrawingConfiguration && !AssertionUtils.isNullOrUndefined(listDrawing?.commercialDimensionsInMM?.heightInMM)) {
    commercialDimensions = {
      heightInMM: listDrawing?.commercialDimensionsInMM?.heightInMM,
      widthInMM: listDrawing?.commercialDimensionsInMM?.widthInMM
    };
  }

  switch (drawing.drawingType) {
    case DrawingType.BMP:
      productionScheduleItemInPathGroup = new BmpProductionScheduleItemInPathGroup(
        drawing as BmpDrawing,
        defaultQuantity,
        commercialDimensions,
        technicalDimensions,
        finishingForProductionSchedule,
        true,
        null,
        null,
        null,
        null,
        null,
        orderLineId
      );

      break;
    case DrawingType.EP:
      const epDrawing: EpDrawing = drawing as EpDrawing;

      productionScheduleItemInPathGroup = new EpProductionScheduleItemInPathGroup(
        drawing as EpDrawing,
        defaultQuantity,
        commercialDimensions,
        technicalDimensions,
        epDrawing.cutbarsLength,
        true,
        null,
        null,
        null,
        null,
        null,
        orderLineId
      );
      break;
    case DrawingType.MANUAL_NON_PRODUCTION_ITEM:
      productionScheduleItemInPathGroup = new JuteProductionScheduleItemInPathGroup(
        drawing as ManualNonProductionItemDrawing,
        commercialDimensions,
        technicalDimensions,
        null,
        null,
        null,
        null,
        null,
        null
      );
      break;
  }

  return productionScheduleItemInPathGroup;
}
